<script setup lang="ts">
import DOMPurify from 'isomorphic-dompurify';
import { computed, type HTMLAttributes } from 'vue';

/**
 * UiProse - A component for rendering sanitized HTML content with prose typography.
 * 
 * Uses isomorphic-dompurify for HTML sanitization (works in both SSR and client).
 * Applies the `prose` class for consistent typography styling.
 * 
 * @example
 * ```vue
 * <UiProse :html="reviewRequest.message" />
 * <UiProse :html="description" class="max-w-none" />
 * ```
 */

export interface UiProseProps {
    /**
     * The HTML content to render. Will be sanitized before rendering.
     */
    html: string | null | undefined;
    
    /**
     * The HTML tag to use for the container element.
     * @default 'div'
     */
    as?: keyof HTMLElementTagNameMap;
    
    /**
     * Additional classes to apply to the prose container.
     * The `prose` class is always applied.
     */
    class?: HTMLAttributes['class'];
}

const props = withDefaults(defineProps<UiProseProps>(), {
    as: 'div',
});

/**
 * Sanitized HTML content using DOMPurify.
 * Returns empty string if html prop is null/undefined/empty.
 */
const sanitizedHtml = computed(() => {
    if (!props.html) return '';
    return DOMPurify.sanitize(props.html);
});
</script>

<template>
    <component 
        :is="props.as" 
        v-if="sanitizedHtml"
        class="prose" 
        :class="props.class"
        :innerHTML="sanitizedHtml"
    />
</template>
